import type { KeyboardEvent } from 'react';

/**
 * Avança o foco para o próximo campo ao pressionar Enter — dá comportamento ao
 * botão "Próximo"/"Seguinte" do teclado mobile (use junto de
 * `inputProps={{ enterKeyHint: 'next' }}`). Ignora textarea (Enter = quebra de
 * linha) e o último campo (deixa o Enter seguir seu curso, ex.: submeter).
 *
 * O handler vai no `onKeyDown` do TextField (o evento borbulha do input); o
 * container dos campos deve ter o atributo `data-form-teclado` para delimitar a
 * sequência de campos daquele formulário.
 */
export function avancarNoEnter(e: KeyboardEvent<HTMLDivElement>): void {
  if (e.key !== 'Enter') return;
  const alvo = e.target as HTMLElement;
  // Enter em textarea é quebra de linha — não navega.
  if (alvo.tagName === 'TEXTAREA') return;

  const proximo = proximoCampo(alvo);
  if (proximo) {
    // Só intercepta quando há um próximo campo; no último, deixa o Enter seguir.
    e.preventDefault();
    proximo.focus();
  }
}

/**
 * Próximo campo focável do mesmo formulário (`[data-form-teclado]`), na ordem
 * do DOM. `null` quando o campo é o último ou está fora de um formulário
 * demarcado. Usado pelo Enter e pela tecla OK do teclado na tela.
 */
export function proximoCampo(alvo: HTMLElement): HTMLElement | null {
  const container = alvo.closest('[data-form-teclado]');
  if (!container) return null;

  // Campos visíveis e focáveis, na ordem do DOM.
  const campos = Array.from(
    container.querySelectorAll<HTMLElement>('input:not([type="hidden"]):not([disabled]), textarea:not([disabled])'),
  ).filter((el) => el.tabIndex !== -1 && el.offsetParent !== null);

  const indice = campos.indexOf(alvo);
  if (indice === -1) return null;
  return campos[indice + 1] ?? null;
}
